import { useContext, useEffect, useState } from "react";
import type { dealerSearchIncoming } from "@dealer-center/ddc-pages-account-search-interface";
import type { UseLocalStorageReducerDispatch } from "@dealer-center/core-components";
import {
  useLocalStorageReducer,
  useLocalStorageReducerActions
} from "@dealer-center/core-components";
import { useLocalStorageKeys } from "@dealer-center/core-interfaces-components";
import type { AccountSearchViewTabs } from "./Tabs/types";
import { AccountSearchTabs } from "./Tabs/Tabs";
import { AccountSearchContext } from "@dealer-center/ddc-pages-account-search-context";
import { useNavigate, useParams } from "react-router-dom";
import {
  useAccountFavourites,
  useAccountHistory
} from "@dealer-center/ddc-api-account-fetch";
interface AccountSearchViewInterface {
  accountSearchFavoritesPersisted: [
    dealerSearchIncoming[],
    UseLocalStorageReducerDispatch<dealerSearchIncoming>
  ];
  tabsEnabled: {
    historyEnabled: boolean;
    favoritesEnabled: boolean;
    setHistoryEnabled: (enabled: boolean) => void;
    setFavoritesEnabled: (enabled: boolean) => void;
  };
  shouldShowFullWidthTable: boolean;
}

export const AccountSearchViewBottom = ({
  accountSearchFavoritesPersisted,
  tabsEnabled,
  shouldShowFullWidthTable
}: AccountSearchViewInterface) => {
  const { tab } = useParams();
  const navigate = useNavigate();
  const activeTab = tab as AccountSearchViewTabs;
  const [accountSearchHistory, dispatchAccountSearchHistory] =
    useLocalStorageReducer<dealerSearchIncoming>(
      useLocalStorageKeys.ddcAccountSearchHistory
    );
  const [accountSearchFavorites] = accountSearchFavoritesPersisted;
  const { accountFavorites } = useAccountFavourites();
  const { accountHistory } = useAccountHistory();

  const { isSearchDefault, searchRequest } = useContext(AccountSearchContext);

  const setActiveTabInt = (tabInt: AccountSearchViewTabs) => {
    navigate(`/account-search/${tabInt}`);
  };
  useEffect(() => {
    if (!isSearchDefault) return;
    let tabToNavigateTo: AccountSearchViewTabs = "results";
    setShouldShowResults(false);
    if (
      (accountFavorites?.length ?? 0) > 0 &&
      activeTab !== "history" &&
      activeTab !== "searchFavorites" &&
      activeTab !== "searchHistory"
    ) {
      tabToNavigateTo = "favorites";
    } else if (
      (accountHistory?.length ?? 0) > 0 &&
      activeTab !== "favorites" &&
      activeTab !== "searchFavorites" &&
      activeTab !== "searchHistory"
    ) {
      tabToNavigateTo = "history";
    } else if (
      accountSearchFavorites.length > 0 &&
      activeTab !== "history" &&
      activeTab !== "favorites" &&
      activeTab !== "searchHistory"
    ) {
      tabToNavigateTo = "searchFavorites";
    } else if (
      accountSearchHistory.length > 0 &&
      activeTab !== "history" &&
      activeTab !== "favorites" &&
      activeTab !== "searchFavorites"
    ) {
      tabToNavigateTo = "searchHistory";
    } else {
      tabToNavigateTo = "results";
      setShouldShowResults(true);
    }
    if (activeTab === tabToNavigateTo) return;
    navigate(`/account-search/${tabToNavigateTo}`);
  }, [
    accountFavorites?.length,
    accountHistory?.length,
    accountSearchFavorites.length,
    accountSearchHistory.length,
    activeTab,
    isSearchDefault,
    navigate
  ]);

  useEffect(() => {
    if (!isSearchDefault) {
      dispatchAccountSearchHistory({
        action: useLocalStorageReducerActions.add,
        payload: searchRequest
      });
      setShouldShowResults(true);
      if (activeTab !== "results") navigate(`/account-search/results`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatchAccountSearchHistory, isSearchDefault, navigate, searchRequest]);
  const [shouldShowResults, setShouldShowResults] = useState(false);

  if (!activeTab) {
    return null;
  }
  return (
    <AccountSearchTabs
      accountSearchFavorites={{
        items: accountSearchFavorites,
        enabled: tabsEnabled.favoritesEnabled,
        persisted: accountSearchFavoritesPersisted,
        setEnabled: tabsEnabled.setFavoritesEnabled
      }}
      accountSearchHistory={{
        items: accountSearchHistory,
        enabled: tabsEnabled.historyEnabled,
        setEnabled: tabsEnabled.setHistoryEnabled,
        dispatch: dispatchAccountSearchHistory
      }}
      activeTab={activeTab}
      setActiveTab={setActiveTabInt}
      shouldShowFullWidthTable={shouldShowFullWidthTable}
      shouldShowResults={shouldShowResults}
    />
  );
};
